import {useEffect} from 'react';

import {TabKind, daysUntil, expiryLabel, initials, sourceLabel, tabKindOf} from '../lib/aggregate';
import {kindHeading} from '../lib/chip-style';
import {GrantView} from '../lib/grant-view';

interface Props {
    open: boolean;
    email: string | null;
    name: string | null;
    kind: TabKind;
    grants: GrantView[];
    onClose: () => void;
    onRevoke: (grant: GrantView) => void;
    onAddGrant: () => void;
    onExtendAll: () => void;
    onRevokeAll: () => void;
}

function formatExpiry(iso: string | undefined): string {
    if (!iso) {
        return 'Permanent';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: '2-digit'});
}

function timeLeftLabel(iso: string | undefined): string {
    if (!iso) {
        return '∞';
    }
    return expiryLabel(daysUntil(iso));
}

function GrantSection({
    title,
    grants,
    onRevoke,
}: {
    title: string;
    grants: GrantView[];
    onRevoke: (g: GrantView) => void;
}) {
    return (
        <section className="drawer-section">
            <h4>{title}</h4>
            {grants.length === 0 ? (
                <div className="empty-opt" style={{padding: '12px 0'}}>
                    None.
                </div>
            ) : (
                grants.map(g => (
                    <div className="drawer-row" key={g.id}>
                        <div style={{minWidth: 0, flex: 1}}>
                            <div className="res" title={g.resource}>
                                {g.resource === '*'
                                    ? 'All tenants (wildcard)'
                                    : g.resourceName ?? g.resource}
                            </div>
                            <div className="meta">
                                <span className={`src-dot src-${g.source}`} />
                                <span>{sourceLabel(g.source)}</span>
                                <span>·</span>
                                <span>{formatExpiry(g.expiresAt)}</span>
                            </div>
                        </div>
                        <span className="when">{timeLeftLabel(g.expiresAt)}</span>
                        <button className="btn btn-danger-text" onClick={() => onRevoke(g)} type="button">
                            Revoke
                        </button>
                    </div>
                ))
            )}
        </section>
    );
}

export function UserDrawer({
    open,
    email,
    name,
    kind,
    grants,
    onClose,
    onRevoke,
    onAddGrant,
    onExtendAll,
    onRevokeAll,
}: Props) {
    useEffect(() => {
        if (!open) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const userInitials = email ? initials(name ?? '', email) : '';
    const hasNonPermanent = grants.some(g => !!g.expiresAt);

    return (
        <>
            <div className={`drawer-backdrop${open ? ' open' : ''}`} onClick={onClose} aria-hidden />
            <aside className={`drawer${open ? ' open' : ''}`} role="dialog" aria-modal aria-hidden={!open}>
                {email && (
                    <>
                        <div className="drawer-head">
                            <span className="avatar lg" aria-hidden>
                                {userInitials}
                            </span>
                            <div style={{flex: 1, minWidth: 0}}>
                                <div className="title">{name || email}</div>
                                <div className="sub">{email}</div>
                            </div>
                            <button className="btn-icon" onClick={onClose} type="button" aria-label="Close">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M6 6l12 12M18 6L6 18"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="drawer-body">
                            {kind === 'users' || kind === 'tenants' ? (
                                <>
                                    <GrantSection
                                        title={`${kindHeading('imp-ai')} grants`}
                                        grants={grants.filter(g => tabKindOf(g.kind) === 'imp-ai')}
                                        onRevoke={onRevoke}
                                    />
                                    <GrantSection
                                        title={`${kindHeading('data-access')} grants`}
                                        grants={grants.filter(g => tabKindOf(g.kind) === 'data-access')}
                                        onRevoke={onRevoke}
                                    />
                                    <GrantSection
                                        title={`${kindHeading('imp-decisions')} grants`}
                                        grants={grants.filter(g => tabKindOf(g.kind) === 'imp-decisions')}
                                        onRevoke={onRevoke}
                                    />
                                </>
                            ) : (
                                <GrantSection
                                    title={`${kindHeading(kind)} grants`}
                                    grants={grants}
                                    onRevoke={onRevoke}
                                />
                            )}
                        </div>

                        <div className="drawer-actions">
                            <button className="btn btn-primary" onClick={onAddGrant} type="button">
                                Add grant
                            </button>
                            <button
                                className="btn btn-outlined"
                                onClick={onExtendAll}
                                type="button"
                                disabled={!hasNonPermanent}
                            >
                                Extend all
                            </button>
                            <div style={{flex: 1}} />
                            <button
                                className="btn btn-danger-text"
                                onClick={onRevokeAll}
                                type="button"
                                disabled={grants.length === 0}
                            >
                                Revoke all
                            </button>
                        </div>
                    </>
                )}
            </aside>
        </>
    );
}
